import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "@icons";
import type { LucideIcon } from "@icons";
import classNames from "classnames";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

export type InlineMessageTone = "success" | "info" | "caution" | "error";

/** One entry in a field-error list — the backend 422 shape. */
export type InlineMessageItem = string | { field?: string; message: string };

export interface InlineMessageAction {
  label: ReactNode;
  onClick?: () => void;
  /** solid (filled), ghost (outlined, default), or link (inline underline). */
  variant?: "solid" | "ghost" | "link";
  icon?: ReactNode;
}

export interface InlineMessageProps {
  /** Colour + icon + default aria role. */
  tone?: InlineMessageTone;
  /** Optional bold heading. */
  title?: ReactNode;
  /** Body content. */
  children?: ReactNode;
  /** Body content shorthand; used when `children` is not provided. */
  message?: ReactNode;
  /** Field-error list (e.g. a 422 response); rendered as bullets with optional field badges. */
  items?: InlineMessageItem[];
  /** Buttons/links rendered below the body. */
  actions?: InlineMessageAction[];
  /** Show a × that hides the message and calls {@link onDismiss}. */
  dismissible?: boolean;
  onDismiss?: () => void;
  /** Dense single-line layout (icon + text). */
  compact?: boolean;
  /** Replace the tone icon. */
  icon?: ReactNode;
  /** Override the aria role (defaults: error/caution → "alert", else "status"). */
  role?: "alert" | "status" | "none";
  /** Accessible label for the dismiss button. */
  dismissLabel?: string;
  className?: string;
}

interface ToneStyle {
  Icon: LucideIcon;
  container: string;
  icon: string;
  title: string;
  strong: string;
  bullet: string;
  fieldBadge: string;
  dismiss: string;
  solid: string;
  ghost: string;
}

const TONES: Record<InlineMessageTone, ToneStyle> = {
  success: {
    Icon: CircleCheck,
    container: "bg-success-subtle border-divider",
    icon: "text-success",
    title: "text-success",
    strong: "text-success",
    bullet: "bg-success",
    fieldBadge: "bg-success-subtle text-success",
    dismiss: "text-success hover:bg-success-subtle",
    solid: "bg-success text-on-primary hover:bg-success",
    ghost: "border-default text-success hover:bg-success-subtle",
  },
  info: {
    Icon: Info,
    container: "bg-info-subtle border-divider",
    icon: "text-primary",
    title: "text-primary",
    strong: "text-primary",
    bullet: "bg-info",
    fieldBadge: "bg-info-subtle text-primary",
    dismiss: "text-primary hover:bg-info-subtle",
    solid: "bg-primary text-on-primary hover:bg-primary-hover",
    ghost: "border-default text-primary hover:bg-info-subtle",
  },
  caution: {
    Icon: TriangleAlert,
    container: "bg-warning-subtle border-divider",
    icon: "text-warning",
    title: "text-warning",
    strong: "text-warning",
    bullet: "bg-warning",
    fieldBadge: "bg-warning-subtle text-warning",
    dismiss: "text-warning hover:bg-warning-subtle",
    solid: "bg-warning text-on-primary hover:bg-warning",
    ghost: "border-default text-warning hover:bg-warning-subtle",
  },
  error: {
    Icon: CircleAlert,
    container: "bg-danger-subtle border-divider",
    icon: "text-danger",
    title: "text-danger",
    strong: "text-danger",
    bullet: "bg-danger",
    fieldBadge: "bg-danger-subtle text-danger",
    dismiss: "text-danger hover:bg-danger-subtle",
    solid: "bg-danger text-on-primary hover:bg-danger",
    ghost: "border-default text-danger hover:bg-danger-subtle",
  },
};

/**
 * Contextual, block-level feedback placed inline where it belongs (below a
 * field, above a submit button, atop a section) — the in-context alternative to
 * a detached toast. Four tones, an optional title, body, a field-error list for
 * 422 responses, action buttons, a compact single-line mode, and an optional
 * dismiss ×.
 */
export function InlineMessage({
  tone = "info",
  title,
  children,
  message,
  items,
  actions,
  dismissible = false,
  onDismiss,
  compact = false,
  icon,
  role,
  dismissLabel,
  className,
}: InlineMessageProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const body = children ?? message;
  const style = TONES[tone];
  const Icon = style.Icon;
  const defaultRole =
    tone === "error" || tone === "caution" ? "alert" : "status";
  const ariaRole = role === "none" ? undefined : (role ?? defaultRole);

  return (
    <div
      role={ariaRole}
      className={classNames(
        "flex gap-[11px] rounded-lg border font-sans leading-normal text-default",
        compact
          ? "items-center px-[11px] py-2 text-[0.78125rem]"
          : "items-start px-[13px] py-3 text-[0.8125rem]",
        style.container,
        className,
      )}
    >
      <span
        className={classNames(
          "flex shrink-0",
          !compact && "mt-[0.5px]",
          style.icon,
        )}
      >
        {icon ?? <Icon size={compact ? 16 : 17} strokeWidth={2} />}
      </span>

      <div
        className={classNames(
          "min-w-0 flex-1",
          compact && "flex items-baseline gap-1.5",
        )}
      >
        {title && (
          <div
            className={classNames(
              "font-semibold leading-snug",
              compact ? "text-[0.78125rem]" : "text-[0.8125rem]",
              style.title,
            )}
          >
            {title}
          </div>
        )}

        {body && (
          <div
            className={classNames(
              "text-default",
              title && !compact && "mt-[3px]",
            )}
          >
            {body}
          </div>
        )}

        {items && items.length > 0 && (
          <ul className="mt-[7px] flex flex-col gap-[5px]">
            {items.map((item, index) => {
              const field = typeof item === "object" ? item.field : undefined;
              const message = typeof item === "object" ? item.message : item;
              return (
                <li key={index} className="relative pl-4 text-default">
                  <span
                    className={classNames(
                      "absolute left-[3px] top-[8px] h-1 w-1 rounded-full",
                      style.bullet,
                    )}
                  />
                  {field && (
                    <span
                      className={classNames(
                        "mr-1 rounded-sm px-[5px] py-px font-mono text-[0.71875rem] font-semibold",
                        style.fieldBadge,
                      )}
                    >
                      {field}
                    </span>
                  )}
                  {message}
                </li>
              );
            })}
          </ul>
        )}

        {actions && actions.length > 0 && (
          <div className="mt-[11px] flex flex-wrap items-center gap-2">
            {actions.map((action, index) => (
              <button
                key={index}
                type="button"
                onClick={action.onClick}
                className={classNames(
                  "inline-flex items-center gap-1.5 text-[0.78125rem] font-semibold transition-colors",
                  action.variant === "solid"
                    ? classNames(
                        "min-h-target rounded border border-transparent px-3",
                        style.solid,
                      )
                    : action.variant === "link"
                      ? classNames(
                          "relative hit-target underline decoration-1 underline-offset-2",
                          style.strong,
                        )
                      : classNames(
                          "min-h-target rounded border px-3",
                          style.ghost,
                        ),
                )}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {dismissible && (
        <button
          type="button"
          aria-label={dismissLabel ?? t("dismiss")}
          data-target-gap={compact ? "compact-dismiss" : undefined}
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          className={classNames(
            "relative grid h-6 w-6 shrink-0 place-items-center rounded opacity-70 transition-opacity hover:opacity-100",
            compact
              ? "hit-target-min -mr-[3px]"
              : "hit-target -mr-[3px] -mt-[3px]",
            style.dismiss,
          )}
        >
          <X size={16} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

export default InlineMessage;
